import {
  SkeletonButton,
  SkeletonPageHeader,
  SkeletonPanel,
  SkeletonRect,
  SkeletonText,
} from '../Skeleton';

// =============================================================================
// Media management (Playlists) skeleton.
// Mirrors the real 3-pane layout as closely as possible:
//   - left sidebar  : 278px playlist tree
//   - center        : "플레이리스트" boxed header + settings/items panels
//   - right drawer  : resizable library drawer spanning full height
// =============================================================================

export function MediaSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-0 w-full gap-4 bg-bg px-4 pb-4">
      <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden">
        <div className="flex-none px-2 pt-6">
          <SkeletonPageHeader />
        </div>
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
      {/* Playlist sidebar */}
      <aside className="flex w-[278px] flex-none flex-col overflow-hidden rounded-md border border-line bg-panel">
        <header className="flex items-center gap-2 border-b border-line px-3.5 py-3">
          <SkeletonText width={70} height="0.85em" />
          <SkeletonText width={20} height="0.85em" />
          <div className="ml-auto flex items-center gap-1">
            <SkeletonRect width={22} height={22} />
            <SkeletonRect width={22} height={22} />
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-1.5 overflow-auto p-2">
          {/* folders with nested rows */}
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 rounded-sm px-1.5 py-1 pr-9">
                <SkeletonRect width={12} height={12} />
                <SkeletonText width={110 - i * 15} height="0.95em" className="flex-1" />
                <SkeletonText width={16} height="0.75em" />
              </div>
              <div className="flex flex-col gap-1.5 pl-2">
                {Array.from({ length: 2 }).map((_, j) => (
                  <div
                    key={j}
                    className="flex flex-col gap-1 rounded border border-line px-2.5 py-2"
                  >
                    <SkeletonText width="85%" height="0.95em" />
                    <SkeletonText width="45%" height="0.8em" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* ungrouped rows */}
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`u-${i}`}
              className="flex flex-col gap-1 rounded border border-line px-2.5 py-2"
            >
              <SkeletonText width="90%" height="0.95em" />
              <SkeletonText width="50%" height="0.8em" />
            </div>
          ))}
        </div>
      </aside>

      {/* Center editor — wrapped in a single bordered box like schedule page */}
      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-line bg-panel">
        <header className="flex flex-none items-center gap-3 border-b border-line bg-bg px-[22px] py-3">
          <SkeletonText width={80} height="0.75em" />
          <SkeletonText width={140} height="1.15em" />
          <SkeletonText width={120} height="0.75em" />
        </header>
        <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-[22px]">
          {/* Playlist info panel */}
          <SkeletonPanel
            header
            title={
              <span className="flex items-baseline gap-2">
                <SkeletonText width={100} height="1em" />
                <SkeletonText width={90} height="0.85em" />
              </span>
            }
            trailing={
              <div className="flex items-center gap-2">
                <SkeletonButton width={70} height={30} />
                <SkeletonButton width={70} height={30} />
              </div>
            }
            className="flex-none"
          >
            <div className="flex flex-wrap items-end gap-6 p-4">
              <div className="flex flex-col gap-1.5">
                <SkeletonText width={60} height="0.75em" />
                <div className="flex items-center gap-1">
                  <SkeletonRect width={60} height={34} />
                  <SkeletonRect width={60} height={34} />
                  <SkeletonRect width={60} height={34} />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <SkeletonText width={80} height="0.75em" />
                <SkeletonRect width={120} height={38} />
              </div>
            </div>
          </SkeletonPanel>

          {/* Items panel */}
          <SkeletonPanel
            header
            title={<SkeletonText width={120} height="1em" />}
            trailing={
              <div className="flex items-center gap-2">
                <SkeletonButton width={110} height={30} />
                <SkeletonButton width={80} height={30} />
              </div>
            }
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex flex-col gap-1 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border border-line bg-panel-2 px-2.5 py-2"
                >
                  <SkeletonRect width={16} height={16} />
                  <SkeletonText width={24} height="0.85em" />
                  <SkeletonRect width={56} height={36} />
                  <SkeletonText width="25%" height="0.95em" className="flex-1" />
                  <SkeletonText width={60} height="0.85em" />
                  <SkeletonText width={40} height="0.85em" />
                  <SkeletonRect width={60} height={28} />
                  <SkeletonRect width={22} height={22} />
                </div>
              ))}
            </div>
          </SkeletonPanel>
        </main>
      </section>
        </div>
      </div>

      {/* Resizable library drawer — spans full height */}
      <div className="-mx-2 -mb-4 w-2 flex-none cursor-col-resize bg-bg">
        <div className="mx-auto h-full w-px bg-line" />
      </div>
      <aside className="mt-4 flex w-[320px] flex-none flex-col self-stretch overflow-hidden rounded-md border border-line bg-panel">
        <header className="flex flex-col gap-3 border-b border-line px-3 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <SkeletonRect width={70} height={28} />
              <SkeletonRect width={70} height={28} />
            </div>
            <div className="flex items-center gap-1">
              <SkeletonRect width={28} height={28} />
              <SkeletonRect width={28} height={28} />
            </div>
          </div>
          <div className="flex min-h-7 flex-wrap items-center gap-1.5">
            <SkeletonRect className="h-7 min-w-[8rem] flex-1" />
            <SkeletonText width={24} height="0.8em" />
            <SkeletonRect width={80} height={28} />
            <SkeletonRect width={32} height={28} />
            <SkeletonRect width={32} height={28} />
          </div>
        </header>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <SkeletonText width={90 - i * 10} height="0.85em" />
                <SkeletonRect width={16} height={16} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 4 }).map((_, j) => (
                  <SkeletonRect key={j} height={72} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
